import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/layout/Navigation';
import {
  User,
  Phone,
  Mail,
  Globe,
  MapPin,
  Briefcase,
  GraduationCap,
  FileText,
  Save,
  ArrowLeft,
  Building,
  Camera,
  Upload,
  Building2,
  CheckCircle,
} from 'lucide-react';
import { isChronicEnabled } from '../config/appMode';
import {
  MEDICAL_DOCTOR_TYPES,
  MEDICAL_SPECIALIST_TYPES,
  FITNESS_SPECIALIST_TYPES,
} from '../config/specialtyTypes';


interface BusinessLocationState {
  fromBusiness?: boolean;
  centerType?: string;
  centerName?: string;
  phone?: string;
  website?: string;
  county?: string;
  selectedPlan?: string;
  businessMeta?: string;
}

export default function SpecialistProfileEdit() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const businessState = (location.state as BusinessLocationState) || {};

  // Form state — pre-fill from business registration if available
  const [title, setTitle] = useState('');
  const [specialtyType, setSpecialtyType] = useState('');
  const [phone, setPhone] = useState(businessState.phone || '');
  const [email, setEmail] = useState(user?.email || '');
  const [website, setWebsite] = useState(businessState.website || '');
  
  // Location
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [county, setCounty] = useState(businessState.county || '');
  
  // Professional details
  const [description, setDescription] = useState(
    businessState.centerName ? `Centru: ${businessState.centerName}` : ''
  );
  const [education, setEducation] = useState('');
  const [experience, setExperience] = useState('');
  const [specializations, setSpecializations] = useState('');
  const [acceptsPatients, setAcceptsPatients] = useState(true);
  
  // Avatar
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [, setLoadingProfile] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [, setExistingProfileId] = useState<string | null>(null);

  // Load existing profile on mount
  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.id) return;

      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/specialists?status=APPROVED`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.ok) {
          const data = await response.json();
          const myProfile = data.specialists?.find((s: any) => s.submittedById === user.id);
          
          if (myProfile) {
            // Populate form with existing data
            setExistingProfileId(myProfile.id);
            setTitle(myProfile.title || '');
            setSpecialtyType(myProfile.specialtyType || '');
            setPhone(myProfile.phone || '');
            setEmail(myProfile.email || user.email || '');
            setWebsite(myProfile.website || '');
            setAddress(myProfile.address || '');
            setCity(myProfile.city || '');
            setCounty(myProfile.county || '');
            setDescription(myProfile.description || '');
            setEducation(myProfile.education || '');
            setExperience(myProfile.experience || '');
            setSpecializations(myProfile.specializations?.join(', ') || '');
            setAcceptsPatients(myProfile.acceptsNewPatients !== false);
            
            if (myProfile.avatarUrl) {
              setAvatarPreview(`${import.meta.env.VITE_API_URL}${myProfile.avatarUrl}`);
            }
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoadingProfile(false);
      }
    };

    loadProfile();
  }, [user]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Te rog selectează o imagine validă.');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Imaginea este prea mare. Maxim 5MB.');
        return;
      }
      
      setAvatarFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!title || !specialtyType || !phone || !address || !city || !county) {
      setError('Te rog completează toate câmpurile obligatorii.');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('accessToken');
      
      // Upload avatar first if provided
      let avatarUrl = '';
      if (avatarFile) {
        const formData = new FormData();
        formData.append('file', avatarFile);
        
        const uploadResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/specialists/upload-avatar`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!uploadResponse.ok) {
          throw new Error('Eroare la încărcarea imaginii');
        }

        const uploadData = await uploadResponse.json();
        avatarUrl = uploadData.avatarUrl;
      }

      // Create specialist profile
      const profileData = {
        name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Specialist',
        title,
        specialtyType,
        phone,
        email: email || undefined,
        website: website || undefined,
        avatarUrl: avatarUrl || undefined,
        address,
        city,
        county,
        description: description || undefined,
        education: education || undefined,
        experience: experience || undefined,
        acceptsNewPatients: acceptsPatients,
        specializations: specializations.split(',').map(s => s.trim()).filter(Boolean)
      };

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/specialists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Eroare la salvare');
      }

      const data = await response.json();
      setSuccess(data.message || 'Profil salvat cu succes!');
      setTimeout(() => navigate('/specialists'), 2000);
    } catch (err: any) {
      setError(err.message || 'Eroare la salvare. Încearcă din nou.');
    } finally {
      setLoading(false);
    }
  };

  const romanianCounties = [
    'Alba', 'Arad', 'Argeș', 'Bacău', 'Bihor', 'Bistrița-Năsăud', 'Botoșani',
    'Brăila', 'Brașov', 'București', 'Buzău', 'Călărași', 'Caraș-Severin',
    'Cluj', 'Constanța', 'Covasna', 'Dâmbovița', 'Dolj', 'Galați', 'Giurgiu',
    'Gorj', 'Harghita', 'Hunedoara', 'Ialomița', 'Iași', 'Ilfov', 'Maramureș',
    'Mehedinți', 'Mureș', 'Neamț', 'Olt', 'Prahova', 'Sălaj', 'Satu Mare',
    'Sibiu', 'Suceava', 'Teleorman', 'Timiș', 'Tulcea', 'Vâlcea', 'Vaslui', 'Vrancea'
  ];

  // În FII FIT (FITNESS) afișăm doar specializări fitness; în modul CHRONIC
  // afișăm și doctori, și specialiști medicali — totul vine din configul
  // centralizat `frontend/src/config/specialtyTypes.ts`.
  const _chronicMode = isChronicEnabled();
  const toOptions = (map: Record<string, string>) =>
    Object.entries(map).map(([value, label]) => ({ value, label }));

  const doctorSpecialtyTypes = _chronicMode ? toOptions(MEDICAL_DOCTOR_TYPES) : [];
  const specialistSpecialtyTypes = _chronicMode
    ? toOptions(MEDICAL_SPECIALIST_TYPES)
    : toOptions(FITNESS_SPECIALIST_TYPES);


  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/specialist/dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Înapoi la Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Completează Profilul Tău Profesional
          </h1>
          <p className="text-gray-600">
            {_chronicMode
              ? 'Informațiile tale vor fi afișate pe hartă și vor fi vizibile pentru pacienți'
              : 'Informațiile tale vor fi afișate pe hartă și vor fi vizibile pentru clienții pasionați de fitness'}
          </p>

        </div>

        {/* Business onboarding banner */}
        {businessState.fromBusiness && (
          <div className="mb-6 p-4 bg-teal-50 border border-teal-200 rounded-xl flex items-start gap-3">
            <Building2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-teal-800 text-sm">
                Bun venit, {businessState.centerName}! 🎉
              </p>
              <p className="text-xs text-teal-700 mt-0.5">
                Contul tău FII FIT Business a fost creat cu succes. Completează profilul de mai jos pentru a apărea în harta specialiștilor.
                Plan ales: <strong>{businessState.selectedPlan}</strong> · Județ: <strong>{businessState.county}</strong>
              </p>
            </div>
            <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-600 text-sm">{success}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Avatar Upload */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Camera className="w-5 h-5 mr-2 text-blue-600" />
                Fotografie Profil (opțional)
              </h3>
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
                {/* Preview */}
                <div className="flex-shrink-0">
                  {avatarPreview ? (
                    <img
                      src={avatarPreview}
                      alt="Avatar preview"
                      className="w-32 h-32 rounded-full object-cover border-4 border-gray-200"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center">
                      <Camera className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Upload Button */}
                <div className="flex-1 w-full">
                  <label className="block">
                    <div className="flex items-center justify-center px-6 py-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition">
                      <div className="text-center">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-1">
                          <span className="font-medium text-blue-600">Încarcă o fotografie</span> sau trage aici
                        </p>
                        <p className="text-xs text-gray-500">
                          PNG, JPG până la 5MB
                        </p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        className="hidden"
                      />
                    </div>
                  </label>
                  {avatarFile && (
                    <p className="mt-2 text-sm text-green-600 flex items-center">
                      ✓ {avatarFile.name}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Basic Info */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <User className="w-5 h-5 mr-2 text-blue-600" />
                Informații de Bază
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {_chronicMode ? 'Titlu (Dr., Prof., etc.) *' : 'Titlu / Funcție *'}
                  </label>
                  <input
                    type="text"
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={_chronicMode ? 'Dr.' : 'Antrenor Personal'}
                  />

                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specialitate *
                  </label>
                  <select
                    required
                    value={specialtyType}
                    onChange={(e) => setSpecialtyType(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Selectează specialitatea...</option>
                    {_chronicMode ? (
                      <>
                        <optgroup label="── Doctori ──">
                          {doctorSpecialtyTypes.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="── Specialiști ──">
                          {specialistSpecialtyTypes.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </optgroup>
                      </>
                    ) : (
                      <optgroup label="── Specialiști Fitness ──">
                        {specialistSpecialtyTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>

                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Phone className="w-5 h-5 mr-2 text-green-600" />
                Date de Contact
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefon *
                  </label>
                  <input
                    type="tel"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+40 123 456 789"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email (opțional)
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="email@exemplu.ro"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Globe className="w-4 h-4 inline mr-1" />
                    Website (opțional)
                  </label>
                  <input
                    type="url"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://..."
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-red-600" />
                Locație
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building className="w-4 h-4 inline mr-1" />
                    Adresa completă *
                  </label>
                  <input
                    type="text"
                    required
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Strada, Număr, Etaj, etc."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Oraș *
                    </label>
                    <input
                      type="text"
                      required
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="București"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Județ *
                    </label>
                    <select
                      required
                      value={county}
                      onChange={(e) => setCounty(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Selectează județul...</option>
                      {romanianCounties.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Professional Details */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Briefcase className="w-5 h-5 mr-2 text-purple-600" />
                Detalii Profesionale
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FileText className="w-4 h-4 inline mr-1" />
                    Descriere (opțional)
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Scurtă descriere despre practica ta..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <GraduationCap className="w-4 h-4 inline mr-1" />
                    Educație (opțional)
                  </label>
                  <textarea
                    value={education}
                    onChange={(e) => setEducation(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: Licență Sport & Fitness, Certificări Antrenor Personal, Nutriție Sportivă..."

                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Experiență (opțional)
                  </label>
                  <textarea
                    value={experience}
                    onChange={(e) => setExperience(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ani de experiență, locuri de muncă anterioare..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specializări (separate prin virgulă, opțional)
                  </label>
                  <input
                    type="text"
                    value={specializations}
                    onChange={(e) => setSpecializations(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={
                      _chronicMode
                        ? 'Dermatomiozită, Lupus, Artrită'
                        : 'Slăbire, Tonifiere, Cardio, Forță, Mobilitate'
                    }
                  />

                </div>

                <div className="flex items-center">
                  <input
                    id="acceptsPatients"
                    type="checkbox"
                    checked={acceptsPatients}
                    onChange={(e) => setAcceptsPatients(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="acceptsPatients" className="ml-3 text-sm text-gray-700">
                    {_chronicMode ? 'Accept pacienți noi' : 'Accept clienți noi'}
                  </label>

                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 sm:gap-4 pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate('/specialist/dashboard')}
                className="w-full sm:w-auto px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                Anulează
              </button>
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5 mr-2" />
                {loading ? 'Se salvează...' : 'Salvează Profilul'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
